/**
 * Agent of Agents — chat panel.
 *
 * Claude-Code-style: no labels, no avatars, no bubbles. User input is
 * shown with a `>` prefix in muted color (terminal-like); assistant
 * replies render as clean markdown directly in the document flow. Tool
 * calls are inset one-liners. Tight 12-14px vertical rhythm between
 * turns. Input bar uses the platform `ChatInput` for parity with the
 * rest of the app.
 */

import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { message as antdMessage, Switch, Tooltip } from 'antd';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '@/services/api';
import { ChatInput } from '@modules/conversations/components/ChatInput';
import type { BrandableFormat } from '@modules/conversations/components/ArtifactSettings';
import type { AoAMessage, TimelineItem } from '../types';
import {
  emptyState,
  hydrateFromMessages,
  reduceTimeline,
  stoppedTurnOf,
  turnShowsText,
  type TimelineState,
} from '../timelineReducer';
import { aoaApi } from '../services/aoaApi';
import {
  continueAoAMessage,
  streamAoAContinuationFromProposal,
  streamAoAMessage,
  streamAoAMessageWithFiles,
} from '../services/aoaStream';
import { SkillBadges } from '@modules/conversations/components/SkillBadges';
import { LiveStatusLine } from '@modules/conversations/components/LiveStatusLine';
import { PausedAnswer } from '@modules/conversations/components/PausedAnswer';
import { ContextMeter } from '@modules/conversations/components/ContextMeter';
import { CompactionMarker } from '@modules/conversations/components/CompactionMarker';
import { resolveLiveStatusLabel } from '@modules/conversations/utils/progressStatus';
import { stepLabelForTool, cardCategoryOf } from '../proposalLabels';
import { extractEvalRef } from '../evalToolResult';
import { stripEchoedSignals } from '../systemSignal';
import {
  batchStillSettling as batchStillSettlingRule,
  type SettlingCard,
} from '../batchSettling';
import { shouldContinueFrom } from '../proposalContinuation';
import { ProposalMarker } from './ProposalMarker';
import { useLanguage } from '@hooks/useLanguage';
import { useMetaAgentModel } from '@hooks/useMetaAgentModel';
import botService from '@modules/chatbots/services/botService';
import { useInspector } from './InspectorContext';
import { ToolCallBlock } from './ToolCallBlock';
import { FileAttachmentCard } from '@modules/conversations/components/FileAttachmentCard';
import { WebSources } from '@/components/common/WebSources';
import { reconcileStoppedTurn, settleStoppedTurn, STOP_RECONCILE_DELAY_MS } from '@/utils/stopReconcile';
import type { StoppedTurn } from '@/utils/stopReconcile';
import conversationService, { ApiError } from '@modules/conversations/services/conversationService';
import { showUploadErrorModal, UploadError } from '@modules/conversations/services/fileValidationService';
import type { SendOutcome } from '@modules/conversations/utils/returnedDraft';

/** The most the stream waits, after a Stop, for the server to have the count
 *  of what was on screen before the socket is closed. */
const STOP_COUNT_WAIT_MS = 1500;
import type { AoAProposal, AoAProposalResolveResponse, ProposalResolution } from '../types';
import { emitJourneyEvent, JOURNEY_EVENTS } from '@modules/journey/detect';
import type { DriveFileRef, ContextUsage } from '@/types/models';
import { TurnGoogleAccessCard, useTurnGoogleAccess } from '@/modules/cloud-storage/components/TurnGoogleAccess';
import './AoAChatPanel.css';
import { useSupportConversation } from '@/components/layout/supportConversation';

interface AoAChatPanelProps {
  /** The tenant's AoA model slot (default + models users can pick). */
  modelConfig?: { default_model: string | null; allowed_models: string[] } | null;
  /** The model this conversation last ran on (`conversation_context.current_model`). */
  initialModel?: string | null;
  conversationId: string;
  initialMessages?: AoAMessage[];
  /** Past proposals for this conversation keyed by proposal_id. Used
   *  to re-render resolved proposal cards on session reopen / refresh
   *  so the user sees the same Accept / Edit / Reject history they
   *  left. The page fetches this via aoaApi.listProposals(conversationId)
   *  in parallel with the messages. */
  initialProposalsById?: Record<string, AoAProposal>;
  /** The meter's opening reading, from GET /agent-of-agents/conversations/<id>. */
  initialContextUsage?: ContextUsage | null;
  onTitleUpdated?: (conversationId: string, title: string) => void;
  onTurnComplete?: () => void;
}

function reducer(
  state: TimelineState,
  action: { type: 'event'; payload: any } | { type: 'reset'; payload: TimelineState },
): TimelineState {
  if (action.type === 'reset') return action.payload;
  return reduceTimeline(state, action.payload);
}

// Must match AoA's backend default at
// backend/shared/services/meta_agent_models.py (META_AGENT_DEFAULTS
// ['agent_of_agents'], consumed via aoa_conversation._resolve_provider_and_model).
// The FAMILY the user sees pre-selected in the picker is the same family the
// backend will actually run on if they don't change it — no drift between
// client default and server default. This is a stable FAMILY key (the concrete
// version it runs is pinned per tenant server-side), NOT a concrete model id —
// pickers only ever deal in family keys now.
// The family is 'haiku' because Claude Haiku 4.5 follows the long AoA persona
// far more reliably than the small OpenAI minis — no internal-monologue leak
// and no sentence-doubling (switched 2026-07-06 after gpt-5-mini leaked
// reasoning). Family key, so the tenant's pinned Haiku release is what runs.

/** Family catalog entry from GET /ai/models/ — the fields this panel needs
 *  to label model refs (family names for the model-switch divider). */
interface ModelFamilyInfo {
  id: string;
  name: string;
  resolved_model?: string;
  /** Every release in this family — lets a legacy ref still match. */
  version_ids?: string[];
  /** Switched-off family keys this family runs ("gpt" on GPT Terra). */
  aliases?: string[];
  enabled?: boolean;
}

export const AoAChatPanel: React.FC<AoAChatPanelProps> = ({
  conversationId,
  initialMessages,
  initialProposalsById,
  initialContextUsage,
  modelConfig,
  initialModel,
  onTitleUpdated,
  onTurnComplete,
}) => {
  const { t } = useLanguage();
  useSupportConversation(conversationId);   // a support report sent from here names the meta-agent
  const [state, dispatch] = useReducer(reducer, undefined, () => emptyState());
  // The latest timeline for handlers that must not be rebuilt on every chunk.
  const itemsRef = useRef(state.items);
  itemsRef.current = state.items;
  // Streams the user stopped. The socket stays open until the server has the
  // count of what was on screen (see handleStopGeneration), and what arrives
  // meanwhile is read but never shown — the screen froze at the click.
  const stoppedStreamsRef = useRef(new WeakSet<AbortController>());
  const [sending, setSending] = useState(false);
  // A Drive tool in the running turn waiting for more Google access.
  const turnAccess = useTurnGoogleAccess(sending);
  // The picker shows the person's pick, else the model the server runs this
  // conversation on; a turn carries a model ONLY when it was picked here.
  const { shownModel, requestedModel, pick: pickModel } = useMetaAgentModel(
    initialModel, modelConfig?.default_model,
  );
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  // The context meter — same contract as the regular chat: seeded from the
  // conversation GET, re-read when the picker changes, kept current by the
  // stream's `context_usage` events.
  const [contextUsage, setContextUsage] = useState<ContextUsage | null>(initialContextUsage ?? null);
  const [compacting, setCompacting] = useState(false);
  useEffect(() => {
    setContextUsage(initialContextUsage ?? null);
  }, [initialContextUsage, conversationId]);
  useEffect(() => {
    // Re-sized only for a pick: otherwise the conversation GET's reading is
    // already sized for the model the server runs.
    if (!conversationId || !requestedModel) return;
    let cancelled = false;
    aoaApi.getContextUsage(conversationId, requestedModel)
      .then((usage) => { if (!cancelled) setContextUsage(usage); })
      .catch(() => { /* the meter is advisory */ });
    return () => { cancelled = true; };
  }, [conversationId, requestedModel]);

  // Family catalog — used to label the model-switch divider with family
  // names. Persisted messages carry the CONCRETE model that actually ran
  // (or, historically, a legacy concrete id), so refs are mapped back to
  // their family via `id` OR `resolved_model`; refs matching neither are
  // rendered as-is through the cosmetic fallback below.
  const [modelFamilies, setModelFamilies] = useState<ModelFamilyInfo[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await api.get('/ai/models/');
        const list: ModelFamilyInfo[] = (response.data.models || []).map((m: any) => ({
          id: m.id,
          name: m.name || m.id,
          resolved_model: m.resolved_model || undefined,
          version_ids: m.version_ids || undefined,
          aliases: m.aliases || undefined,
          enabled: m.enabled,
        }));
        if (!cancelled) setModelFamilies(list);
      } catch {
        // Non-fatal — divider labels fall back to cosmetic normalization.
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const familyByRef = useMemo(() => {
    // utils/modelLabels.findCatalogEntry's order: key or pin, then a
    // switched-off key it runs ("gpt" on GPT Terra), then any release; the
    // first family to claim a ref keeps it (a Terra release is GPT's and
    // GPT Terra's — the one pinned to it names it).
    const m = new Map<string, ModelFamilyInfo>();
    const claim = (ref: string, f: ModelFamilyInfo) => { if (!m.has(ref)) m.set(ref, f); };
    for (const f of modelFamilies) {
      claim(f.id, f);
      if (f.resolved_model) claim(f.resolved_model, f);
    }
    for (const f of modelFamilies) for (const a of f.aliases || []) claim(a, f);
    for (const f of modelFamilies) for (const v of f.version_ids || []) claim(v, f);
    return m;
  }, [modelFamilies]);
  const displayModelName = useCallback(
    (raw: string): string => familyByRef.get(raw)?.name ?? legacyModelDisplayName(raw),
    [familyByRef],
  );

  // Side-panel state. Proposals fire into the inspector instead of
  // rendering inline; the chat only shows the small collapsed marker.
  // Reading `active` lets ProposalMarker know if it should render in
  // the "this one is open in the panel" highlighted state.
  const inspector = useInspector();
  // chatbot_id -> display name, so a simulation opens titled correctly instead
  // of flashing a placeholder. Populated on first lookup and reused after that.
  const agentNameCacheRef = useRef<Map<string, string>>(new Map());
  // The simulation id the panel is currently showing, so a slow async name
  // lookup can tell whether it is still the relevant one.
  const openSimIdRef = useRef<string | null>(null);
  const resolveAgentName = useCallback(async (chatbotId: string): Promise<string> => {
    const cached = agentNameCacheRef.current.get(chatbotId);
    if (cached) return cached;
    try {
      const bot = await botService.getBot(chatbotId);
      const name = String((bot as any)?.chatbot_name || '').trim();
      if (name) agentNameCacheRef.current.set(chatbotId, name);
      return name;
    } catch {
      // Non-fatal: the real name arrives with the tool result anyway. A
      // neutral header for a moment beats a wrong one.
      return '';
    }
  }, []);

  const activeProposalIdInPanel =
    inspector.active && inspector.active.kind === 'proposal'
      ? inspector.active.proposal.proposal_id
      : null;

  // Once a reopened conversation's files have loaded, make the newest one the
  // panel's subject - but do NOT open the panel. Throwing it open on every page
  // load would interrupt someone who came back only to read the chat; the
  // collapsed rail's badge is how they find the file when they want it. Only
  // fills an EMPTY slot, so it can never displace a card the user is answering.
  const hydratedArtifactId = inspector.artifacts.activeId;
  const inspectorHasItem = inspector.active !== null;
  useEffect(() => {
    if (hydratedArtifactId && !inspectorHasItem) {
      inspector.setActiveArtifact(hydratedArtifactId, { expand: false, source: 'restore' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires on hydration only
  }, [hydratedArtifactId]);

  /**
   * Wrap dispatch so proposal lifecycle events also flow into the
   * inspector. Single source of truth for the side-panel state lives
   * in InspectorContext; the timeline reducer still also tracks the
   * proposal so the chat can render the inline marker. The two are
   * kept in sync by this wrapper at the event-receipt seam.
   */
  const dispatchEvent = useCallback((ev: any) => {
    if (ev?.type === 'context_usage') {
      setContextUsage(ev as ContextUsage);
      return;
    }
    // A Drive tool waiting for more Google access: the card, not the timeline.
    if (turnAccess.onStreamEvent(ev)) return;
    dispatch({ type: 'event', payload: ev });
    if (ev?.type === 'done') {
      // Never leave a file stuck mid-write. `artifact_start` fires on the tool
      // CALL, so a turn that ended without a matching render - the agent asked
      // a question instead of finishing, or the stream was stopped - would
      // otherwise leave a card spinning forever.
      void inspector.artifacts.reconcileGenerating();
    }
    if (ev?.type === 'proposal_emitted') {
      // Build the proposal shape the inspector expects from the SSE
      // payload — same fields the reducer uses, plus defaults for the
      // fields that only come from the REST resolve response.
      const p: AoAProposal = {
        proposal_id: ev.proposal_id,
        tool_name: ev.tool_name,
        target_entity_type: ev.target_entity_type,
        target_entity_id: ev.target_entity_id ?? null,
        field: ev.field ?? null,
        old_value: ev.old_value ?? null,
        new_value: ev.new_value ?? null,
        proposed_payload: (ev.proposed_payload ?? null) as Record<string, unknown> | null,
        edited_payload: null,
        rationale: ev.rationale ?? null,
        status: 'pending',
        reject_reason: null,
        worker_hint: ev.worker_hint ?? null,
        created_at: ev.created_at ?? new Date().toISOString(),
        resolved_at: null,
      } as AoAProposal;
      inspector.pushProposal(p);
      // Auto-accept mode: apply the card the instant it lands. The sweep effect
      // below is the safety net for anything this fast path misses.
      void autoAcceptRef2.current?.(p);
    }
    if (ev?.type === 'artifact_start' || ev?.type === 'artifact_render'
        || ev?.type === 'artifact_show' || ev?.type === 'artifact_error') {
      // The meta-agent is writing (or has finished, or is showing) a file.
      // Same events, same reducer and same panel a regular chat uses.
      // `artifact_start` fires on the tool CALL, which is what makes the panel
      // open at the same moment the agent says it is writing rather than after
      // a silent pause; `artifact_show` (show_artifact) opens an existing file
      // without rebuilding it.
      inspector.artifacts.handleEvent(ev as any);
      const fileId = (ev as any).artifact_file_id
        || (ev as any).artifact?.artifact_file_id;
      if (fileId && ev.type !== 'artifact_error') {
        inspector.setActiveArtifact(fileId, { source: 'stream' });
      }
    }
    if (ev?.type === 'library_file_preview') {
      // The agent opened a stored Library file for the user — show it on the
      // Files tab with a download button. Access was checked server-side.
      const anyEv = ev as any;
      if (anyEv.file_id && anyEv.file_name) {
        inspector.setLibraryPreview({
          file_id: String(anyEv.file_id),
          file_name: String(anyEv.file_name),
          file_type: anyEv.file_type ? String(anyEv.file_type) : undefined,
        });
      }
    }
    if (ev?.type === 'proposal_resolved') {
      // Build a minimal updated proposal to push into the inspector so
      // the active card's localStatus flips immediately and the queue
      // chip refreshes. We don't have the full row from the SSE — the
      // panel's ProposalCard will refetch via the resolve REST call,
      // but this gives instant feedback in the marker.
      const stub: AoAProposal = {
        proposal_id: ev.proposal_id,
        tool_name: '',                     // marker doesn't need it
        target_entity_type: '' as any,
        target_entity_id: null,
        field: null,
        old_value: null,
        new_value: null,
        proposed_payload: null,
        edited_payload: null,
        rationale: null,
        status: ev.status,
        reject_reason: null,
        worker_hint: null,
        created_at: new Date().toISOString(),
        resolved_at: new Date().toISOString(),
      } as AoAProposal;
      inspector.updateProposal(stub);
    }
    // Live feedback for send_test_message_to_agent — make the panel
    // react the INSTANT the AoA decides to send, not 5-10 seconds
    // later when the target agent's reply lands. Two hooks:
    //
    //   - tool_debug action='call':
    //       Args carry user_message and (for continuations)
    //       eval_conversation_id. We optimistically push the user
    //       message into the panel + mark the tool as in-flight so a
    //       "typing…" indicator shows for the wait.
    //
    //   - tool_debug action='result':
    //       Tool is done; clear the in-flight flag, run the regular
    //       auto-open / refetch path so the persisted assistant reply
    //       lands in the panel.
    // The eval tool announces itself on the meta stream the moment it starts,
    // with the REAL eval id and the REAL agent name — no synthetic id, no name
    // lookup (which fails under RLS for a cross-tenant agent and left the
    // header on "Preparing simulation…" for the whole turn). Then it streams
    // the target agent's reply, and finally announces the persisted row with a
    // structured event, so the panel's refresh never depends on scraping an
    // id out of a truncated display preview.
    if (ev?.type === 'simulation_started' && ev.eval_conversation_id) {
      const evalId = String(ev.eval_conversation_id);
      const chatbotId = ev.target_chatbot_id ? String(ev.target_chatbot_id) : null;
      const name = String(ev.target_agent_name || '').trim();
      if (name && chatbotId) agentNameCacheRef.current.set(chatbotId, name);
      if (chatbotId) {
        // Retire the synthetic placeholder the tool_debug call may have opened.
        inspector.setToolInFlight(`pending-${chatbotId}`, false);
        inspector.clearPendingUserMessage(`pending-${chatbotId}`);
      }
      openSimIdRef.current = evalId;
      inspector.setActiveSimulation({
        kind: 'simulation',
        eval_conversation_id: evalId,
        target_chatbot_id: chatbotId,
        target_agent_name: name,
        scrollToMessageId: null,
      });
      if (ev.user_message) inspector.setPendingUserMessage(evalId, String(ev.user_message));
      inspector.clearSimulationDraft(evalId);
      inspector.setToolInFlight(evalId, true);
      inspector.setCollapsed(false);
    }
    if (ev?.type === 'simulation_chunk' && ev.eval_conversation_id) {
      inspector.appendSimulationDraft(String(ev.eval_conversation_id), String(ev.content || ''));
    }
    if (ev?.type === 'simulation_reply' && ev.eval_conversation_id) {
      const evalId = String(ev.eval_conversation_id);
      openSimIdRef.current = evalId;
      inspector.setActiveSimulation({
        kind: 'simulation',
        eval_conversation_id: evalId,
        target_chatbot_id: ev.target_chatbot_id ? String(ev.target_chatbot_id) : null,
        target_agent_name: String(ev.target_agent_name || '').trim(),
        scrollToMessageId: ev.assistant_message_id ? String(ev.assistant_message_id) : null,
      });
    }
    if (
      ev?.type === 'tool_debug' &&
      ev.action === 'call' &&
      ev.tool === 'send_test_message_to_agent'
    ) {
      const args = (ev.args || {}) as {
        user_message?: string;
        eval_conversation_id?: string;
        chatbot_id?: string;
      };
      const evalId = args.eval_conversation_id?.trim();
      const chatbotId = args.chatbot_id?.trim() || null;
      if (evalId) {
        // Continuation: panel is already open for this eval. Just push
        // the optimistic user bubble + flip the typing indicator on.
        openSimIdRef.current = evalId;
        if (args.user_message) {
          inspector.setPendingUserMessage(evalId, args.user_message);
        }
        inspector.setToolInFlight(evalId, true);
      } else if (chatbotId) {
        // First call of a brand-new eval — we don't have the real
        // eval_conversation_id yet (the tool hasn't run). Open the
        // panel with a synthetic id derived from the chatbot id so
        // the user sees their request immediately. SimulationPanelView
        // skips the API fetch when the id starts with 'pending-' and
        // renders the optimistic user bubble + typing indicator only.
        // On tool result, we'll setActiveSimulation with the REAL id —
        // which causes a brief remount, but the visible content is
        // largely the same.
        const synthId = `pending-${chatbotId}`;
        // Open with the agent's REAL name. It used to open with the literal
        // string 'agent', and BotAvatar derives both its initial and its colour
        // from the name — so the panel showed a stranger's avatar under the
        // wrong title, then visibly flipped when the tool result landed. An
        // empty name renders a neutral header instead of a wrong one, and the
        // lookup below fills it in almost immediately.
        const knownName = agentNameCacheRef.current.get(chatbotId) || '';
        openSimIdRef.current = synthId;
        inspector.setActiveSimulation({
          kind: 'simulation',
          eval_conversation_id: synthId,
          target_chatbot_id: chatbotId,
          target_agent_name: knownName,
          scrollToMessageId: null,
        });
        if (!knownName) {
          void resolveAgentName(chatbotId).then((name) => {
            if (!name) return;
            // The agent's real reply can land before this lookup resolves. Re-
            // opening the synthetic id then would throw away the loaded
            // conversation and put the panel back on the placeholder, which
            // renders nothing (SimulationPanelView skips the fetch for
            // 'pending-' ids).
            if (openSimIdRef.current !== synthId) return;
            inspector.setActiveSimulation({
              kind: 'simulation',
              eval_conversation_id: synthId,
              target_chatbot_id: chatbotId,
              target_agent_name: name,
              scrollToMessageId: null,
            });
          });
        }
        if (args.user_message) {
          inspector.setPendingUserMessage(synthId, args.user_message);
        }
        inspector.setToolInFlight(synthId, true);
        inspector.setCollapsed(false);
      }
    }
    if (
      ev?.type === 'tool_debug' &&
      ev.action === 'result' &&
      ev.tool === 'send_test_message_to_agent'
    ) {
      // NOTE: we deliberately do NOT clear toolInFlight here. The
      // SimulationPanelView clears it atomically with the freshly-
      // refetched messages landing in state — that prevents the
      // "old messages standing alone briefly while new ones load"
      // flash that read as a re-render. The synthetic `pending-<chat>`
      // id used during first-eval calls still needs cleaning, though,
      // because setActiveSimulation will swap to the real eval id and
      // the pending sim view unmounts.
      const continuationEvalId = (ev.args?.eval_conversation_id as string | undefined)?.trim();
      const callChatbotId = (ev.args?.chatbot_id as string | undefined)?.trim();
      if (!continuationEvalId && callChatbotId) {
        const synthId = `pending-${callChatbotId}`;
        inspector.setToolInFlight(synthId, false);
        inspector.clearPendingUserMessage(synthId);
      }
      const raw = ev.preview ?? ev.result;
      // One shared extractor for both call sites (the other is the inline
      // pill in ToolCallBlock). It tolerates the preview being truncated
      // mid-JSON, which is the normal case for a long agent reply.
      const metadata = extractEvalRef(raw);
      if (metadata && metadata.eval_conversation_id) {
        console.info('[AoA] opening simulation in inspector', {
          eval_conversation_id: metadata.eval_conversation_id,
          target_agent_name: metadata.target_agent_name,
        });
        const evalId = String(metadata.eval_conversation_id);
        openSimIdRef.current = evalId;
        inspector.setActiveSimulation({
          kind: 'simulation',
          eval_conversation_id: evalId,
          target_chatbot_id:
            metadata.target_chatbot_id ??
            (ev.args?.chatbot_id as string | undefined) ??
            null,
          target_agent_name: String(metadata.target_agent_name || 'agent'),
          // Phase 3 scroll anchor: scroll the panel to the assistant
          // message this turn just produced. Falls back to null
          // (bottom-scroll) when the id isn't available.
          scrollToMessageId: metadata.target_assistant_message_id ?? null,
        });
        // Tool finished — but DO NOT clear the optimistic / typing state
        // here. The SimulationPanelView clears them atomically with the
        // refetched messages so the transition is seamless (no "old
        // messages standing alone" flash). The setActiveSimulation call
        // above bumps the refresh nonce which kicks off that refetch.
      } else {
        // Loud failure log so we can see if the metadata extraction
        // is the broken link in the chain.
        console.warn('[AoA] simulation tool_debug result had no parseable eval_conversation_id', {
          preview_head: typeof ev.preview === 'string' ? ev.preview.slice(0, 200) : null,
          raw_kind: typeof (ev.preview ?? ev.result),
        });
      }
    }

    // `open_agent_simulation` used to be sniffed out of the tool_debug stream
    // here and opened the side panel in a bespoke "setup" mode. It now emits an
    // ordinary proposal (`propose_simulation_tenant`), so it arrives through
    // the same proposal_emitted path as every other card and needs no special
    // case — the tenant question is asked where all the other questions are.
  }, [inspector, turnAccess.onStreamEvent]);

  // Outbound message queue — when the user submits another message while the
  // agent is still streaming, we don't reject it; we queue it and drain after
  // the current turn completes. Same pattern as Claude Code's UX.
  const queueRef = useRef<string[]>([]);
  const [queueLen, setQueueLen] = useState(0);
  const updateQueueLen = useCallback(() => setQueueLen(queueRef.current.length), []);

  // The live status line — a running, human sentence of what the agent is
  // doing RIGHT NOW ("Reading the style guide…", "Connecting a folder to an
  // agent…"), so a long step reads as progress rather than a stuck spinner.
  // Same mechanism the regular chat uses (LiveStatusLine: cross-fading text +
  // an elapsed counter). The step for a tool comes from the last still-pending
  // tool call, whose name and args name the actual operation.
  const activeToolItem = useMemo(() => {
    for (let i = state.items.length - 1; i >= 0; i -= 1) {
      const it = state.items[i];
      if (it.kind === 'tool_call' && it.status === 'pending') return it;
    }
    return null;
  }, [state.items]);
  const activityText = useMemo(() => {
    if (state.progress) {
      // Same sentence (and climbing word count) the regular chat shows.
      return resolveLiveStatusLabel(t, { kind: 'progress', ...state.progress });
    }
    if (state.activity === 'tool') {
      return stepLabelForTool(t, activeToolItem?.tool ?? state.activeTool, activeToolItem?.args);
    }
    if (state.activity === 'typing') return t('metaAgent.chat.activity.typing');
    return t('metaAgent.chat.activity.thinking');
  }, [t, state.activity, state.activeTool, activeToolItem, state.progress]);
  // Restart the elapsed counter each time the step itself changes (a new tool,
  // or a switch between thinking/typing/tool) — never per render.
  const stepKey = state.activity === 'tool'
    ? (activeToolItem?.invocationId ?? state.activeTool ?? 'tool')
    : (state.activity ?? 'idle');
  const [activitySince, setActivitySince] = useState<number | undefined>(undefined);
  useEffect(() => {
    setActivitySince(state.activity ? Date.now() : undefined);
  }, [stepKey, state.activity]);

  // Artifact branding toggle — the SAME control a regular agent has in its
  // composer. A document the meta-agent writes wears the tenant's design skill
  // (branded pptx/docx) unless a format is switched off here. State is seeded
  // per conversation from the server and persisted on toggle.
  const [brandInfo, setBrandInfo] = useState<{
    available: boolean; formats: BrandableFormat[]; branding: Record<string, boolean>;
  }>({ available: false, formats: [], branding: {} });
  useEffect(() => {
    if (!conversationId) {
      setBrandInfo({ available: false, formats: [], branding: {} });
      return;
    }
    let cancelled = false;
    aoaApi.getArtifactBrand(conversationId)
      .then((r) => {
        if (cancelled) return;
        setBrandInfo({
          available: r.branded_available,
          formats: (r.formats || []) as BrandableFormat[],
          branding: r.branding || {},
        });
      })
      .catch(() => {
        if (!cancelled) setBrandInfo({ available: false, formats: [], branding: {} });
      });
    return () => { cancelled = true; };
  }, [conversationId]);
  const handleBrandToggle = useCallback((format: BrandableFormat, branded: boolean) => {
    // Optimistic: an ABSENT key means branded, so drop it on; set false on off.
    setBrandInfo((prev) => {
      const next = { ...prev.branding };
      if (branded) delete next[format]; else next[format] = false;
      return { ...prev, branding: next };
    });
    if (!conversationId) return;
    aoaApi.setArtifactBranding(conversationId, format, branded)
      .then((b) => setBrandInfo((prev) => ({ ...prev, branding: b })))
      .catch(() => antdMessage.error(t('metaAgent.chat.brandToggleFailed')));
  }, [conversationId, t]);

  // Auto-accept mode — when on, every proposal the meta-agent raises is applied
  // the instant it lands, EXCEPT destructive ones (deletes), which always wait
  // for the person. Persisted per conversation. A ref mirrors the state so the
  // SSE `proposal_emitted` handler (a stable callback) reads the live value.
  const [autoAccept, setAutoAccept] = useState(false);
  const autoAcceptRef = useRef(false);
  useEffect(() => { autoAcceptRef.current = autoAccept; }, [autoAccept]);
  useEffect(() => {
    if (!conversationId) { setAutoAccept(false); return; }
    let cancelled = false;
    aoaApi.getAutoAccept(conversationId)
      .then((on) => { if (!cancelled) setAutoAccept(on); })
      .catch(() => { if (!cancelled) setAutoAccept(false); });
    return () => { cancelled = true; };
  }, [conversationId]);
  // One place that applies a card in auto mode, used by BOTH the fast path
  // (the proposal_emitted event) and the sweep below.
  //
  // A delete is NEVER auto-applied — that guard is the whole reason auto mode
  // is safe. Everything else is attempted once per card; `autoAcceptInflight`
  // stops the fast path and the sweep from racing each other into a double
  // resolve, and `autoAcceptFailed` stops a card that genuinely cannot apply
  // from being retried forever.
  /* Declared here, above the auto-accept path that reads them: the stream
     bookkeeping below is defined later in the component. */
  const sendingRef = useRef(false);
  const deferredContinuationRef = useRef<string[]>([]);
  const runContinuationStreamRef = useRef<((proposalId: string) => Promise<void>) | null>(null);
  const autoAcceptInflight = useRef<Set<string>>(new Set());
  const autoAcceptFailed = useRef<Set<string>>(new Set());
  /* The newest card of the current batch that actually resolved. A card that
     FAILS to apply cannot be continued from — the backend builds the
     continuation out of a resolved proposal — so if the failure is the last
     thing the batch was waiting for, the turn continues from this one
     instead. Without it, one failed resolve swallowed the whole turn's
     continuation and the agent went quiet (caught in review, 2026-09-10). */
  const lastResolvedInBatch = useRef<string | null>(null);
  /* Proposals this panel has ALREADY opened (or parked) a continuation for.
     Several surfaces can report the same resolution — the auto-accept fast
     path and the sweep race each other, the inspector's own card resolves
     independently, and a 409 reconciliation re-reports a card someone else
     resolved. Each of those is allowed to ASK for a continuation; this is
     what stops the turn being resumed twice for one decision, so the ask can
     stay liberal and no path has to stay silent just in case. */
  const continuedFrom = useRef<Set<string>>(new Set());
  /* Cards this panel has SEEN resolve, recorded the instant the resolve
     returns. The inspector's card list is React state and lags by a render,
     so a settle check made in the same tick as a resolve still reads that
     card as pending — and parks a continuation that nothing may ever
     release. This is the panel's own, un-lagged answer to "is it decided?". */
  const resolvedByPanel = useRef<Set<string>>(new Set());
  /* Is anything else in this batch still on its way to being applied? */
  const batchStillSettling = useRef<(excludePid: string) => boolean>(() => false);
  /* Fire the continuation a settling batch parked, once the batch is done.
   *
   * A card that resolves while its siblings are still applying does NOT
   * continue the turn — it parks itself in `lastResolvedInBatch` and waits,
   * so the agent is asked to carry on ONCE, with every card already applied.
   * Something has to notice when that wait is over. The failure path did
   * (a card that cannot apply lets the rest through); the SUCCESS path did
   * not, and instead relied on the last card of the batch observing
   * `batchStillSettling === false` as it resolved.
   *
   * It does not reliably observe that. The fast path and the sweep both
   * reach for the same card, so a duplicate attempt can re-enter it into
   * `autoAcceptInflight` after it has already resolved — and while that
   * entry is there, the genuinely-last card sees a batch that is still
   * settling, parks, and nobody ever looks again. The turn then sits in
   * silence until the user types "continue", which is exactly the report.
   *
   * So: re-check whenever the in-flight set shrinks. Idempotent — it clears
   * the park before firing, and `continuedFrom` is not consulted because
   * this IS the deferred half of a decision already judged continuable. */
  const flushSettledBatch = useRef<() => void>(() => {});
  useEffect(() => {
    resolvedByPanel.current = new Set();
    continuedFrom.current = new Set();
  }, [conversationId]);
  const autoAcceptProposal = useCallback(async (p: AoAProposal) => {
    if (!autoAcceptRef.current) return;
    if (cardCategoryOf(p.tool_name) === 'delete') return;
    const pid = p.proposal_id;
    if (autoAcceptInflight.current.has(pid) || autoAcceptFailed.current.has(pid)) return;
    autoAcceptInflight.current.add(pid);
    try {
      const res = await aoaApi.resolveProposal(pid, { action: 'approve' });
      inspector.updateProposal(res as unknown as AoAProposal);
      // Same continuation path a manual Accept uses (parks if a stream is
      // mid-flight, fires when it ends).
      inspector.proposalResolvedListenerRef.current?.(
        { action: 'approve' } as ProposalResolution,
        res as unknown as AoAProposal,
      );
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = String(err?.response?.data?.error || err?.message || '');
      // BENIGN: the card was already resolved — the fast path and the sweep
      // both reached it. Nothing is wrong, so nothing is said; the card's
      // real status is fetched so the batch can finish on the truth.
      if (status === 409 || /not pending/i.test(detail)) {
        try {
          const fresh = await aoaApi.getProposal(pid);
          inspector.updateProposal(fresh);
          autoAcceptInflight.current.delete(pid);
          inspector.proposalResolvedListenerRef.current?.(
            { action: 'approve' } as ProposalResolution, fresh,
          );
          return;
        } catch {
          /* fall through to the genuine-failure path below */
        }
      }
      // GENUINE: this card is not going to apply by itself. Say so — the
      // turn may now be waiting on something that will never arrive, and
      // silence here is what left the agent looking frozen (caught in
      // review, 2026-09-10). The earlier toast was removed because it also
      // fired on the benign case above, which is now handled separately.
      autoAcceptFailed.current.add(pid);
      console.error('[AoA] auto-accept failed for proposal', pid, err);
      antdMessage.warning(t('metaAgent.chat.autoAcceptCardStuck'), 8);
      autoAcceptInflight.current.delete(pid);
      // And let the rest of the batch through: continue from the newest card
      // that DID resolve, since a failed one cannot be continued from.
      const resumeFrom = lastResolvedInBatch.current;
      if (resumeFrom && !batchStillSettling.current(pid)) {
        lastResolvedInBatch.current = null;
        if (sendingRef.current) {
          if (!deferredContinuationRef.current.includes(resumeFrom)) {
            deferredContinuationRef.current.push(resumeFrom);
          }
        } else {
          // Through the ref: runContinuationStream is declared below this
          // callback (the same reason the ref exists for its own finally).
          void runContinuationStreamRef.current?.(resumeFrom);
        }
      }
      return;
    } finally {
      autoAcceptInflight.current.delete(pid);
      // The batch just got smaller — if that was the last thing it was
      // waiting on, release the continuation it parked.
      flushSettledBatch.current();
    }
  }, [inspector, t]);
  // One definition of "the batch is still settling", used by the resolve
  // path and by the failure path above. A card that cannot apply stops
  // counting (autoAcceptFailed) and a deletion never auto-applies, so a
  // stuck card can never swallow the continuation.
  useEffect(() => {
    batchStillSettling.current = (excludePid: string) => {
      const cards: SettlingCard[] = [];
      inspector.proposalQueue.forEach((p) => {
        cards.push({ proposal_id: p.proposal_id, status: p.status, tool_name: p.tool_name });
      });
      const active = inspector.active;
      if (active?.kind === 'proposal') {
        const p = active.proposal;
        cards.push({ proposal_id: p.proposal_id, status: p.status, tool_name: p.tool_name });
      }
      return batchStillSettlingRule({
        excludePid,
        inflight: [...autoAcceptInflight.current],
        failed: [...autoAcceptFailed.current],
        decided: [...resolvedByPanel.current],
        cards,
        isDelete: (toolName) => cardCategoryOf(toolName) === 'delete',
      });
    };
    // The card list just changed — if a continuation was parked on a view
    // that has now caught up, release it. Cheap, idempotent, and the second
    // guarantee (after the synchronous `decided` set) that a park is never
    // permanent.
    flushSettledBatch.current();
  }, [inspector.proposalQueue, inspector.active]);

  // The success-path counterpart to the failure path's "let the rest
  // through". Runs whenever the in-flight set shrinks; a no-op unless a
  // continuation is actually parked and the batch has genuinely settled.
  useEffect(() => {
    flushSettledBatch.current = () => {
      const resumeFrom = lastResolvedInBatch.current;
      if (!resumeFrom) return;
      if (batchStillSettling.current(resumeFrom)) return;
      // Claim it before firing so a second shrink cannot double-fire.
      lastResolvedInBatch.current = null;
      if (sendingRef.current) {
        if (!deferredContinuationRef.current.includes(resumeFrom)) {
          deferredContinuationRef.current.push(resumeFrom);
        }
        return;
      }
      void runContinuationStreamRef.current?.(resumeFrom);
    };
  }, []);

  // The event handler is a stable callback, so it reaches this through a ref.
  const autoAcceptRef2 = useRef<((p: AoAProposal) => Promise<void>) | null>(null);
  useEffect(() => { autoAcceptRef2.current = autoAcceptProposal; }, [autoAcceptProposal]);

  // SWEEP — the safety net. The fast path only fires on a live proposal_emitted
  // event, so a card that arrived any other way (hydrated from history, pushed
  // while the toggle was still loading, or an event that simply did not reach
  // this handler) would sit PENDING forever while the agent moved on. This
  // reconciles every pending card the panel knows about — the same three places
  // autoRejectPendingProposals collects from.
  useEffect(() => {
    if (!autoAccept) return;
    const pending: AoAProposal[] = [];
    inspector.proposalQueue.forEach((p) => {
      if (p.status === 'pending') pending.push(p);
    });
    if (inspector.active?.kind === 'proposal'
        && inspector.active.proposal.status === 'pending') {
      pending.push(inspector.active.proposal);
    }
    pending.forEach((p) => { void autoAcceptProposal(p); });
  }, [autoAccept, inspector.proposalQueue, inspector.active, autoAcceptProposal]);

  /** "Compact now" from the meter's popover. The marker lands in the
   *  timeline through the same reducer case the live event uses. */
  const handleCompactNow = useCallback(async () => {
    if (!conversationId || compacting) return;
    setCompacting(true);
    try {
      const result = await aoaApi.compactNow(conversationId, requestedModel);
      if (result.usage) setContextUsage(result.usage);
      if (!result.compacted) {
        antdMessage.info(t('chat.context.nothingToCompact'));
        return;
      }
      dispatch({
        type: 'event',
        payload: {
          type: 'compacted',
          summary_message: result.summary_message,
          summarized_count: result.summarized_count ?? 0,
          round: result.round ?? 1,
          tokens_before: result.tokens_before ?? 0,
          tokens_after: result.tokens_after ?? 0,
          folded_message_ids: result.folded_message_ids ?? [],
        },
      });
      // The reducer's `compacted` case marks the stream live; nothing is.
      dispatch({ type: 'event', payload: { type: 'done' } });
      antdMessage.success(t('chat.context.compactedToast', { count: result.summarized_count ?? 0 }));
    } catch {
      antdMessage.error(t('chat.context.compactFailed'));
    } finally {
      setCompacting(false);
    }
  }, [conversationId, compacting, requestedModel, t]);

  const handleAutoAcceptToggle = useCallback((on: boolean) => {
    setAutoAccept(on);  // optimistic
    if (!conversationId) return;
    aoaApi.setAutoAccept(conversationId, on)
      .then((v) => setAutoAccept(v))
      .catch(() => {
        setAutoAccept(!on);
        antdMessage.error(t('metaAgent.chat.autoAcceptToggleFailed'));
      });
  }, [conversationId, t]);
  // When the user scrolls up to read past content we MUST NOT yank them
  // back down on every token. This flag flips on whenever the scroll
  // position is near the bottom (within 80px) and flips off the moment
  // they scroll up. We only auto-scroll when it's true.
  const stickToBottomRef = useRef(true);

  useEffect(() => {
    if (initialMessages && initialMessages.length) {
      dispatch({ type: 'reset', payload: hydrateFromMessages(initialMessages, initialProposalsById) });
      // Files the meta-agent wrote in this conversation, back on screen after a
      // reload. Asked of the conversation as well as of the message
      // attachments, because a turn that ended by a path which skipped the
      // turn-end bind leaves a real file with no attachment pointing at it.
      void inspector.artifacts.hydrateFromMessages(initialMessages, conversationId);
    } else {
      dispatch({ type: 'reset', payload: emptyState() });
    }
    // Reset stickiness when switching conversations.
    stickToBottomRef.current = true;
    requestAnimationFrame(() => {
      const el = scrollerRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, [conversationId, initialMessages, initialProposalsById]);

  // Our OWN smooth scrolls fire scroll events too. Ignore those: mid-animation
  // the distance to the bottom is still large, so measuring it there flipped
  // stickiness OFF during streaming — auto-scroll then stopped for the rest of
  // the turn and new text piled up below the fold, looking like it had slipped
  // under the composer. Only a scroll the USER performs may unstick.
  const suppressScrollCheckUntil = useRef(0);

  const onScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (Date.now() < suppressScrollCheckUntil.current) return;
    const el = e.currentTarget;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distance < 120;
  }, []);

  // Auto-scroll on new content — but ONLY if the user is already at the
  // bottom (or close to it). Smooth behavior so streaming token appends
  // don't read as a jarring page jump.
  useEffect(() => {
    if (!stickToBottomRef.current) return;
    const el = scrollerRef.current;
    if (!el) return;
    // Use rAF so the new content is laid out before we measure.
    requestAnimationFrame(() => {
      // Mute the stickiness check while this animation runs (see onScroll).
      suppressScrollCheckUntil.current = Date.now() + 700;
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    });
    // Depend on the count + the tail item's content length so that
    // streaming `ai_chunk` updates also trigger this effect, not just
    // new items.
  }, [
    state.items.length,
    state.items[state.items.length - 1]?.kind === 'text'
      ? (state.items[state.items.length - 1] as any).content?.length
      : 0,
  ]);

  // Track sending state in a ref so the resolved-callback can read it
  // synchronously without needing to be re-created on every state flip.
  useEffect(() => {
    sendingRef.current = sending;
  }, [sending]);

  // Continuation parked while a stream is mid-flight. Previously an Accept
  // that landed during an active turn was silently DROPPED — the model
  // never resumed and the user had to type "continue" to unstick the chat
  // (observed 2026-08-04). Now the proposal_id waits here and fires from
  // the active stream's finally-path. A ref (not state) because the
  // finally blocks read it synchronously.
  // A QUEUE, not a single slot: cards stay clickable during a stream, so
  // two quick Accepts would otherwise have the second clobber the first
  // and the model would never hear about the overwritten one (review
  // finding 2026-08-04). Drained one per stream-finish, in order.

  // Aborts the turn currently in flight. One controller per stream (user
  // turn OR continuation); nulled in each stream's finally. Handed to
  // fetch, so aborting tears the socket down immediately instead of
  // waiting for the next SSE event — which matters because the meta-agent
  // can sit silent inside a single simulation exchange for tens of
  // seconds. The socket close is also what stops the SERVER: it trips
  // progress_bus.pump_stream's `abandoned` flag, which closes the producer
  // and sets the turn's cancellation token so the next
  // send_test_message_to_agent bails instead of running another exchange.
  const abortRef = useRef<AbortController | null>(null);

  // Single-shot timer for the post-Stop reconcile below. Held in a ref so a
  // second Stop (or leaving the panel) cancels the pending re-read instead
  // of letting a stale one land on a turn it no longer describes.
  const stopReconcileTimerRef = useRef<number | null>(null);
  // Bumped by every Stop and every cancel: a re-read still running for an
  // earlier one lands nothing.
  const stopReconcileSeqRef = useRef(0);
  // A Stop is aborting, but the SERVER has not necessarily noticed yet — it
  // only learns when it next tries to write to the closed pipe. Until then the
  // conversation still has a turn in flight, and nothing server-side refuses a
  // second one, so draining the queue the instant Stop is pressed can open a
  // second stream that interleaves with the first. This ref holds the drain
  // for that window; `drainTick` is what wakes the effect afterwards, since a
  // ref change alone re-renders nothing.
  const stopSettlingRef = useRef(false);
  // A Continue is streaming into a paused answer (see handleContinue).
  const continuingRef = useRef(false);
  const [drainTick, setDrainTick] = useState(0);
  const cancelStopReconcile = useCallback(() => {
    stopReconcileSeqRef.current += 1;
    if (stopReconcileTimerRef.current !== null) {
      window.clearTimeout(stopReconcileTimerRef.current);
      stopReconcileTimerRef.current = null;
    }
    // Never leave the queue held by a timer that is no longer going to fire
    // (conversation switch, unmount-and-return): the hold is an optimisation
    // for one race, not a lock, and failing open is the safe direction.
    stopSettlingRef.current = false;
  }, []);

  /**
   * Ask the server what actually happened to the turn we just aborted: drop
   * the optimistic "Response stopped" note if the answer had in fact
   * completed, and on a genuine stop show what its stored row records — its
   * Sources and the release that served it — under the frozen text. Reuses
   * the SAME endpoint the page hydrates from — no new route; read again only
   * while the turn's row has not landed (settleStoppedTurn, bounded).
   *
   * Why a targeted dispatch instead of re-running `hydrateFromMessages`
   * over the fetched messages (the other obvious option): an aborted turn
   * never reaches the route's `complete` branch, which is where
   * `_persist_tool_calls` stamps `aoa_tool_calls` onto the assistant row.
   * A full re-hydrate would therefore render the turn WITHOUT the tool-call
   * blocks and proposal cards the user just watched stream in — trading a
   * cosmetic lie for visibly disappearing cards. The reducer keeps the
   * live timeline and only corrects what the server knows better.
   *
   * A stopped Continue only ever settles as stopped: its message read back
   * unchanged only means the stop has not been saved yet, and a Continue
   * has no title/summary window in which it could have quietly completed.
   */
  const reconcileStopAgainstServer = useCallback(async (turn: StoppedTurn, seq: number) => {
    if (!conversationId) return;
    const verdict = await settleStoppedTurn(
      async () => (await aoaApi.getConversation(conversationId)).messages || [],
      (messages) => {
        const v = reconcileStoppedTurn(messages, turn);
        return turn.messageId && v.kind === 'completed' ? { kind: 'unknown' as const } : v;
      },
      () => stopReconcileSeqRef.current !== seq,
    );
    if (verdict.kind === 'completed') {
      dispatch({
        type: 'event',
        payload: { type: 'turn_stop_reconciled', content: verdict.content, message: verdict.message, turn },
      });
    } else if (verdict.kind === 'interrupted') {
      dispatch({ type: 'event', payload: { type: 'turn_stop_settled', message: verdict.message, turn } });
    }
    // 'unknown' leaves the note where it is — a failed or inconclusive read
    // is not evidence that the turn finished.
  }, [conversationId]);

  /**
   * Stop the turn in flight.
   *
   * The two queues are treated DIFFERENTLY, and the line between them is who
   * put the work there (operator decision 2026-09-06, narrowing the earlier
   * rule that a Stop cleared both).
   *
   * `queueRef` holds messages the PERSON typed while a turn was running. Those
   * survive a Stop and drain as usual — `setSending(false)` below is what the
   * drain effect watches, so the next one goes out on its own. The reason is
   * what Stop is mostly used FOR: not "you are going wrong" but "you are
   * stuck". A turn that hangs on a slow provider call leaves the user pressing
   * Stop to get out of it, and throwing away everything they typed in the
   * meantime punishes them for the platform's hang. Nothing they wrote should
   * need writing twice.
   *
   * `deferredContinuationRef` holds the agent's own automatic "keep going"
   * after a card. That is still dropped, and that is still the point of Stop:
   * the machine's momentum ends, the person's instructions do not. The cards
   * themselves are untouched — they stay resolved in the DB, and the ledger
   * the model rebuilds every turn still reports them as APPROVED.
   *
   * To stop AND discard what you queued, clear the composer queue; a Stop on
   * an empty queue behaves exactly as it always did.
   */
  const handleStopGeneration = useCallback(() => {
    const controller = abortRef.current;
    abortRef.current = null;
    // Tell the server how much of the answer is on screen (chunks are shown
    // as they arrive here): the stopped turn is stored with no more, though
    // what is in flight keeps reaching its socket until the abort lands.
    // The count must reach the server BEFORE the socket closes: the close is
    // this panel's stop signal, and the turn saves what it has as soon as it
    // sees it. So the stream is aborted after the Stop request is answered,
    // or after STOP_COUNT_WAIT_MS at most; the screen is frozen right away.
    let counted: Promise<void> = Promise.resolve();
    if (controller) stoppedStreamsRef.current.add(controller);
    if (controller && conversationId) {
      // A Continue counts only its own characters: the server adds them to
      // the answer it already has.
      const shownChars = itemsRef.current.reduce(
        (n, it) => (it.kind === 'text' && it.role === 'assistant' && it.streaming
          ? n + Array.from(it.content).length - Array.from(it.continueBase ?? '').length : n),
        0,
      );
      counted = conversationService.stopGeneration(conversationId, shownChars);
    }
    // The stored row this stop will be read back from — none when nothing
    // was on screen yet (a Stop before the first word stores no row).
    const stoppedTurn = stoppedTurnOf(itemsRef.current);
    const readBack = turnShowsText(itemsRef.current);
    // queueRef is deliberately NOT cleared — see above.
    deferredContinuationRef.current = [];
    // Freeze the partial assistant block and settle any spinning tool row.
    dispatch({ type: 'event', payload: { type: 'turn_stopped' } });
    // Flip the composer back to idle NOW rather than waiting for the stream's
    // finally. This is also what DRAINS the queue: the drain effect watches
    // `sending` going false, so the next thing the user typed goes out on its
    // own from here.
    setSending(false);
    void Promise.race([
      counted,
      new Promise<void>((resolve) => window.setTimeout(resolve, STOP_COUNT_WAIT_MS)),
    ]).then(() => controller?.abort());
    // The note we just painted is a GUESS — the client aborted, which is not
    // the same as the server having been cut off. Settle it against stored
    // truth, shortly. See `stopReconcile.ts` for why every wrong-timing
    // outcome is the safe one.
    cancelStopReconcile();
    const seq = stopReconcileSeqRef.current;
    stopSettlingRef.current = true;
    stopReconcileTimerRef.current = window.setTimeout(() => {
      stopReconcileTimerRef.current = null;
      // The abort has had time to reach the server. Release the queue, then
      // settle the transcript — in that order, so a slow reconcile cannot keep
      // the user's next message waiting.
      stopSettlingRef.current = false;
      setDrainTick((t) => t + 1);
      if (readBack) void reconcileStopAgainstServer(stoppedTurn, seq);
    }, STOP_RECONCILE_DELAY_MS);
  }, [cancelStopReconcile, reconcileStopAgainstServer, conversationId]);
  // Latest runContinuationStream, reachable from its OWN finally block
  // (a const useCallback can't reference itself) and from handleSend's.
  /** Fire a parked continuation, if any. Called wherever a stream finishes
   *  (both finally paths), right after `sending` flips back to false. */
  const drainDeferredContinuation = useCallback(() => {
    const next = deferredContinuationRef.current.shift();
    if (!next) return;
    void runContinuationStreamRef.current?.(next);
  }, []);

  /**
   * Pipe a /continue-from-proposal SSE stream into the same reducer the
   * regular streamAoAMessage path feeds. Reuses the activity indicator,
   * the tool-call rendering, the proposal-card rendering — everything.
   * The only difference: no user_message event is emitted by the
   * backend (the synthetic message is filtered out server-side), so
   * the visible chat shows ONLY the assistant's response.
   */
  const runContinuationStream = useCallback(
    async (proposalId: string) => {
      const controller = new AbortController();
      abortRef.current = controller;
      setSending(true);
      // A continuation that produces no assistant content is indistinguishable
      // from the agent ignoring the user — which is exactly how the
      // DetachedInstanceError in continue_from_proposal presented: HTTP 200,
      // zero events, no error anywhere. Track it and say so.
      let sawContent = false;
      try {
        for await (const ev of streamAoAContinuationFromProposal(
          conversationId,
          { proposal_id: proposalId, model: requestedModel },
          { signal: controller.signal },
        )) {
          if (controller.signal.aborted) break;
          if (stoppedStreamsRef.current.has(controller)) continue;
          if (ev.type === 'ai_chunk' || ev.type === 'ai_complete' || ev.type === 'error') {
            sawContent = true;
          }
          dispatchEvent(ev);
          if (ev.type === 'ai_complete' && ev.title_update?.title && onTitleUpdated) {
            onTitleUpdated(conversationId, ev.title_update.title);
          }
          if (ev.type === 'done') break;
        }
        if (!sawContent && !controller.signal.aborted && !stoppedStreamsRef.current.has(controller)) {
          dispatch({
            type: 'event',
            payload: { type: 'error', error: t('metaAgent.chat.continuationEmpty') },
          });
        }
      } catch (e: any) {
        // A user-initiated Stop surfaces here as an AbortError. That is a
        // normal outcome, not a failure — rendering it would put a red
        // "The user aborted a request" line in the transcript.
        if (!controller.signal.aborted && e?.name !== 'AbortError') {
          dispatch({
            type: 'event',
            payload: { type: 'error', error: e?.message || 'continuation stream error' },
          });
        }
      } finally {
        // A stopped stream stays open until its count reached the server; a
        // turn the user started meanwhile owns the panel now — leave it be.
        const superseded = abortRef.current !== null && abortRef.current !== controller;
        if (abortRef.current === controller) abortRef.current = null;
        if (!superseded) {
          setSending(false);
          // A continuation that arrived while THIS stream ran (user accepted
          // another card mid-turn) is parked in deferredContinuationRef —
          // fire it now instead of leaving the model silent. A Stop has
          // already emptied that queue, so this is a no-op after one.
          drainDeferredContinuation();
          if (onTurnComplete) onTurnComplete();
        }
      }
    },
    [conversationId, requestedModel, onTitleUpdated, onTurnComplete, drainDeferredContinuation, t],
  );

  /**
   * Continue the paused answer at `index` (the conversation's last row) —
   * the regular chat's Continue: the SAME message grows, no user message is
   * written, Stop works as for any turn.
   */
  const handleContinue = useCallback(
    async (index: number, messageId: string) => {
      // `continuingRef` is set synchronously below (`sendingRef` follows the
      // state a render later), so a double click sends one Continue, not two.
      if (!conversationId || sendingRef.current || continuingRef.current) return;
      const controller = new AbortController();
      abortRef.current = controller;
      continuingRef.current = true;
      setSending(true);
      dispatch({ type: 'event', payload: { type: 'continue_start', index } });
      let completed = false;
      // A refusal: NOT_CONTINUABLE, or CONTINUE_IN_PROGRESS (another tab).
      let refused: string | null = null;
      try {
        for await (const ev of continueAoAMessage(conversationId, messageId, { signal: controller.signal })) {
          if (controller.signal.aborted) break;
          if (stoppedStreamsRef.current.has(controller)) continue;
          if (ev.type === 'ai_complete') completed = true;
          if (ev.type === 'error' && (ev.error_code === 'NOT_CONTINUABLE'
                                      || ev.error_code === 'CONTINUE_IN_PROGRESS')) {
            refused = ev.error_code;
            break;
          }
          dispatchEvent(ev);
          if (ev.type === 'done') break;
        }
      } catch (e: any) {
        if (!controller.signal.aborted && e?.name !== 'AbortError') {
          // A refusal before the stream opened (409): say it in words.
          const message = String(e?.message);
          if (/\bHTTP 409\b/.test(message)) {
            refused = message.includes('CONTINUE_IN_PROGRESS') ? 'CONTINUE_IN_PROGRESS' : 'NOT_CONTINUABLE';
          } else {
            dispatch({ type: 'event', payload: { type: 'error', error: e?.message || 'stream error' } });
          }
        }
      } finally {
        continuingRef.current = false;
        if (!completed && !stoppedStreamsRef.current.has(controller)) {
          // Held by another Continue: the answer is still paused if that one fails.
          dispatch({ type: 'event', payload: { type: 'continue_failed', index,
                                               stillPaused: refused !== 'NOT_CONTINUABLE' } });
          if (refused) antdMessage.warning(t(`chat.errors.${refused}`));
        }
        const superseded = abortRef.current !== null && abortRef.current !== controller;
        if (abortRef.current === controller) abortRef.current = null;
        if (!superseded) {
          setSending(false);
          drainDeferredContinuation();
          if (onTurnComplete) onTurnComplete();
        }
      }
    },
    [conversationId, dispatchEvent, onTurnComplete, drainDeferredContinuation, t],
  );

  // Keep the ref pointed at the latest runContinuationStream so the
  // finally-path drain (including this stream's own) never goes stale.
  useEffect(() => {
    runContinuationStreamRef.current = runContinuationStream;
  }, [runContinuationStream]);

  /**
   * Auto-continue after the user resolves a proposal.
   *
   * EVERY deliberate decision resumes the agent — approve, edit AND
   * reject. The backend returns auto_continue=true for all three and we
   * open the /continue-from-proposal SSE stream, so the meta-agent reads
   * the resolution as a synthetic system signal and picks the thread back
   * up. The synthetic message is filtered out of the visible chat, so the
   * user sees: card → decision → agent keeps talking.
   *
   * Reject used to be deliberately silent ("Ctrl-C feel"). It read as the
   * agent freezing: the card was a question, declining it is an answer,
   * and the user had to type "ok, what now?" to restart a conversation
   * that had never ended. The rejection signal tells the model nothing was
   * applied and to ask what to change instead of re-firing the same card.
   *
   * The one auto_continue=false case is the chat's own bookkeeping reject
   * (`silent: true`), where the user's new message already drives the turn.
   */
  const handleProposalResolved = useCallback(
    (_resolution: ProposalResolution, updated: AoAProposal) => {
      const resolved = updated as AoAProposalResolveResponse;
      // Sync the chat timeline immediately from the HTTP resolve response —
      // the bus-published proposal_resolved event is dropped whenever no SSE
      // stream is open, which left inline markers stuck on PENDING after an
      // Accept from the inspector (observed 2026-07-26).
      dispatch({
        type: 'event',
        payload: {
          type: 'proposal_resolved',
          proposal_id: resolved.proposal_id,
          status: resolved.status,
        },
      });
      if (resolved.status && resolved.status !== 'pending') {
        resolvedByPanel.current.add(resolved.proposal_id);
      }
      // Whether this resolution resumes the turn, and whether THIS panel has
      // already resumed from it — see proposalContinuation.ts for why an
      // absent `auto_continue` must not read as false. Claimed here, before
      // any of the parking branches below, so a card cannot be parked by one
      // path and fired again by another.
      if (!shouldContinueFrom(resolved, continuedFrom.current)) return;
      continuedFrom.current.add(resolved.proposal_id);
      // ONE continuation per BATCH of cards, not one per card.
      //
      // Under auto-accept a turn that raises three cards used to resolve them
      // one at a time and kick a continuation after each. The second turn
      // therefore began while the agent's own remaining cards were still
      // settling — it saw one of its three resolved and the others open, and
      // re-raised them to be sure, which the tools then refused as
      // "already in folder" (operator proof runs, 2026-09-10). Waiting for
      // the batch to finish removes that state entirely: the agent is asked
      // to continue once, with every card already applied.
      //
      // Only ever a WAIT, never a drop: a card that cannot apply lands in
      // autoAcceptFailed and stops counting, so a stuck card can never
      // swallow the continuation.
      if (autoAcceptRef.current) {
        if (batchStillSettling.current(resolved.proposal_id)) {
          // Remember it: if the card the batch is waiting on then FAILS,
          // this is what the turn continues from.
          lastResolvedInBatch.current = resolved.proposal_id;
          return;
        }
        lastResolvedInBatch.current = null;
      }
      if (sendingRef.current) {
        // A turn is mid-flight. DON'T drop the continuation — park it and
        // fire when the active stream finishes (its finally-path calls
        // drainDeferredContinuation). Dropping left the AoA looking stuck
        // after an Accept; observed 2026-08-04, user had to type
        // "continue". A repeat resolve for the SAME proposal (e.g. a
        // double-click) is still a no-op — it's already parked.
        if (!deferredContinuationRef.current.includes(resolved.proposal_id)) {
          deferredContinuationRef.current.push(resolved.proposal_id);
        }
        return;
      }
      void runContinuationStream(resolved.proposal_id);
    },
    [runContinuationStream, dispatch],
  );

  // Register the chat's auto-continue handler with the inspector so the
  // side panel's ProposalCard resolves trigger the continuation stream
  // here in the chat. Stored on a ref to avoid re-render churn.
  useEffect(() => {
    inspector.proposalResolvedListenerRef.current = handleProposalResolved;
    return () => {
      // Detach on unmount so a stale chat instance doesn't get fired.
      if (inspector.proposalResolvedListenerRef.current === handleProposalResolved) {
        inspector.proposalResolvedListenerRef.current = null;
      }
    };
  }, [handleProposalResolved, inspector.proposalResolvedListenerRef]);

  // The tenant card hands the run BACK to the meta-agent, and it does so
  // through the SAME continuation stream every other resolved card uses —
  // see handleProposalResolved above.
  //
  // This used to post a synthetic USER message ("Start the simulation of X
  // under the tenant Y. Talk to it yourself: send it your first message…").
  // It worked, but it wrote words into the user's own side of the transcript
  // that they never typed, and re-explained the agent's job to it on every
  // single run. A tool-resolution signal (_simulation_tenant_signal in
  // proposal_service) carries the same instruction without faking a turn.

  // Publish which meta conversation is on screen, so cards rendered inside it
  // can parent what they create to it (the simulation tenant card binds its
  // eval here).
  useEffect(() => {
    inspector.metaConversationIdRef.current = conversationId || '';
  }, [conversationId, inspector.metaConversationIdRef]);

  // Panels hand plain-language requests back to the meta-agent (the file
  // panel's "link this file" actions). Sent as an ordinary turn so the ask is
  // visible in the transcript and the agent answers with the right tool — and,
  // for a real change, an approval card.
  useEffect(() => {
    const askAgent = (message: string) => {
      void handleSendRef.current?.(message);
    };
    inspector.chatRequestListenerRef.current = askAgent;
    return () => {
      if (inspector.chatRequestListenerRef.current === askAgent) {
        inspector.chatRequestListenerRef.current = null;
      }
    };
  }, [inspector.chatRequestListenerRef]);

  // Ref so the resolved callback can call the latest handleSend without
  // needing it as a dependency (avoids a circular memo dependency).
  const handleSendRef = useRef<((content: string) => Promise<SendOutcome>) | null>(null);

  /**
   * Auto-reject any proposal still in 'pending' state. Used when the
   * user types a new message instead of clicking Accept / Edit / Reject
   * on an open card — that implicit "I'm moving on" is treated as a
   * rejection so the previous card collapses to its red state and the
   * meta-agent sees the rejection on its next read of the proposal.
   *
   * Each card is rejected via the real REST API (so the DB row carries
   * status='rejected' across refreshes) AND a synthetic proposal_resolved
   * event is dispatched so the card flips visually without waiting for a
   * round trip through the SSE queue.
   */
  const autoRejectPendingProposals = useCallback(async () => {
    // Collect from the timeline AND the inspector (active slot + waiting
    // list). The timeline alone is not enough: a card displaced into the
    // queue (or restored from history) can be pending in the panel while
    // its timeline item is stale, and those were the cards left sitting in
    // the waiting list after a typed rejection (reported on staging
    // 2026-08-04).
    const ids = new Set<string>(
      state.items
        .filter((it) => it.kind === 'proposal' && it.proposal.status === 'pending')
        .map((it) => (it as { kind: 'proposal'; proposal: AoAProposal }).proposal.proposal_id),
    );
    inspector.proposalQueue
      .filter((p) => p.status === 'pending')
      .forEach((p) => ids.add(p.proposal_id));
    if (
      inspector.active?.kind === 'proposal'
      && inspector.active.proposal.status === 'pending'
    ) {
      ids.add(inspector.active.proposal.proposal_id);
    }
    const pendingIds = [...ids];
    if (pendingIds.length === 0) return;
    // Tell the user. Moving on is treated as a rejection, which is fine as a
    // rule — but silently is not: without this the proposed change just
    // vanishes, and a whole agent can end up with no system prompt because the
    // conversation kept flowing past its cards (observed 2026-07-26).
    antdMessage.info(
      t('metaAgent.chat.discardedProposal', { count: pendingIds.length }),
      5,
    );
    await Promise.all(
      pendingIds.map((pid) =>
        aoaApi
          .resolveProposal(pid, {
            action: 'reject',
            reason: t('metaAgent.autoRejected'),
            silent: true,
          })
          .catch(() => {
            // Reject can fail if the proposal was just resolved by the
            // user clicking a button milliseconds earlier. The card's
            // own UI handles that case; we just don't crash the send.
          })
          .finally(() => {
            dispatch({
              type: 'event',
              payload: { type: 'proposal_resolved', proposal_id: pid, status: 'rejected' },
            });
            // Tell the INSPECTOR too. Its cleanup (drop from the waiting
            // list, auto-advance the panel) keys off updateProposal, and
            // the SSE proposal_resolved that would normally carry it is
            // published while no stream is open — so it is dropped, and
            // the rejected card used to sit in the waiting list forever.
            inspector.updateProposal({
              proposal_id: pid,
              status: 'rejected',
              resolved_at: new Date().toISOString(),
            } as AoAProposal);
          }),
      ),
    );
  }, [state.items, inspector]);

  const handleSend = useCallback(
    async (content: string, attachments?: File[], driveFiles?: DriveFileRef[]): Promise<SendOutcome> => {
      const text = content.trim();
      if (!text && (!attachments || attachments.length === 0) && (!driveFiles || driveFiles.length === 0)) return;
      // Journey detection: the user asked the Agent of Agents for something
      // real — the "build with AoA" Try.
      emitJourneyEvent(JOURNEY_EVENTS.aoaMessageSent);
      // The user's act of sending is an explicit intent to engage with
      // what they just wrote — re-arm sticky-bottom and jump immediately
      // so the new user bubble isn't hidden behind the input bar. Without
      // this, a user who was scrolled mid-history sees their message
      // append off-screen and has to scroll down manually. Two waves:
      //   (a) hard jump NOW so we beat any layout pause on the user_message
      //       event arrival;
      //   (b) the existing auto-scroll effect handles smooth follow-up on
      //       streaming tokens.
      stickToBottomRef.current = true;
      requestAnimationFrame(() => {
        const el = scrollerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      });
      // If the user is typing while a previous proposal is still
      // pending, treat that as an implicit Reject — collapse the card
      // and persist the rejection BEFORE the new message goes out.
      // (Auto-rejection works whether a turn is running or not; we
      // always run it.)
      await autoRejectPendingProposals();
      // Claude-Code-style queue: if a turn is already running, append the
      // new message to the queue and return; the drain effect will fire
      // it after the current stream completes. Files only carry on the
      // active send — queued plain-text messages have no attachments.
      if (sending) {
        queueRef.current.push(text);
        updateQueueLen();
        return;
      }
      const controller = new AbortController();
      abortRef.current = controller;
      setSending(true);
      let outcome: SendOutcome = undefined;
      try {
        const events = (attachments && attachments.length > 0) || (driveFiles && driveFiles.length > 0)
          ? streamAoAMessageWithFiles(
              conversationId,
              { message: text, model: requestedModel, files: attachments ?? [], drive_files: driveFiles },
              { signal: controller.signal },
            )
          : streamAoAMessage(
              conversationId,
              { message: text, model: requestedModel },
              { signal: controller.signal },
            );
        for await (const ev of events) {
          if (controller.signal.aborted) break;
          if (stoppedStreamsRef.current.has(controller)) continue;
          dispatchEvent(ev);
          if (ev.type === 'user_message') {
            // Force a jump to the very bottom the moment the user bubble
            // lands — the streaming auto-scroll effect will take over from
            // the first ai_chunk onward.
            stickToBottomRef.current = true;
            requestAnimationFrame(() => {
              const el = scrollerRef.current;
              if (el) el.scrollTop = el.scrollHeight;
            });
            if (ev.conversation_title && onTitleUpdated) {
              onTitleUpdated(conversationId, ev.conversation_title);
            }
          }
          if (ev.type === 'ai_complete' && ev.title_update?.title && onTitleUpdated) {
            onTitleUpdated(conversationId, ev.title_update.title);
          }
          if (ev.type === 'done') break;
        }
      } catch (e: any) {
        // Refused before the stream opened — a refused file (worded as in
        // every chat) or any other HTTP refusal: the server never took the
        // message, so the composer gives it back.
        if (e instanceof UploadError) {
          showUploadErrorModal(e, t);
          outcome = 'not_taken';
        } else if (e instanceof ApiError) {
          dispatch({ type: 'event', payload: { type: 'error', error: e.message } });
          outcome = 'not_taken';
        } else if (!controller.signal.aborted && e?.name !== 'AbortError') {
          // Stop → AbortError. Expected, not an error to render.
          dispatch({
            type: 'event',
            payload: { type: 'error', error: e?.message || 'stream error' },
          });
        }
      } finally {
        // As in runContinuationStream: a newer turn may own the panel by the
        // time a stopped stream closes.
        const superseded = abortRef.current !== null && abortRef.current !== controller;
        if (abortRef.current === controller) abortRef.current = null;
        if (!superseded) {
          setSending(false);
          // Same deferral drain as runContinuationStream's finally: an Accept
          // that landed while this user-turn streamed resumes the model now.
          // Empty after a Stop, by design.
          drainDeferredContinuation();
          if (onTurnComplete) onTurnComplete();
        }
      }
      return outcome;
    },
    [conversationId, requestedModel, sending, onTitleUpdated, onTurnComplete, autoRejectPendingProposals, drainDeferredContinuation, t],
  );

  // Keep the ref pointed at the latest handleSend for the resolved-callback.
  useEffect(() => {
    handleSendRef.current = handleSend;
  }, [handleSend]);

  // Leaving the page mid-turn is a stop too — abort so the socket closes
  // and the server-side turn is cancelled instead of running on unwatched.
  // A pending stop-reconcile dies with the panel: it would resolve against
  // a timeline that no longer exists.
  useEffect(() => () => {
    abortRef.current?.abort();
    abortRef.current = null;
    cancelStopReconcile();
  }, [cancelStopReconcile]);

  // Switching conversations cancels a pending reconcile for the same reason
  // — the fetch is keyed to the conversation that was stopped, and its
  // verdict must not land on the transcript the user moved to.
  useEffect(() => cancelStopReconcile, [conversationId, cancelStopReconcile]);

  // Drain the message queue after each turn completes. We watch `sending`
  // flipping false and, if there's anything queued, fire the next one.
  useEffect(() => {
    if (sending) return;
    // A Stop is still settling — see `stopSettlingRef`. The queue is kept, not
    // dropped; it goes out when the window closes and `drainTick` wakes this.
    if (stopSettlingRef.current) return;
    if (queueRef.current.length === 0) return;
    const next = queueRef.current.shift()!;
    updateQueueLen();
    // Defer one frame so the just-completed turn's state-write settles
    // before the next turn mounts. Avoids a flash of empty state.
    requestAnimationFrame(() => {
      void handleSendRef.current?.(next);
    });
  }, [sending, drainTick, updateQueueLen]);

  return (
    <div className="aoa-chat">
      <div
        ref={scrollerRef}
        className="aoa-scroller"
        onScroll={onScroll}
        data-testid="aoa-timeline"
      >
        <div className="aoa-content">
          {state.items.length === 0 && !sending && <EmptyPrompt />}
          {state.items.map((it, idx) => {
            // Detect a model switch between consecutive assistant turns
            // and render the same divider banner the regular chat uses.
            // Same rule as the regular chat (utils/modelSwitches): compare the
            // FAMILY, not the spelling (a re-pin inside one family is no
            // switch), skip a reply the failover served, and name each side
            // by the release that answered.
            let modelSwitch: { from: string; to: string } | null = null;
            if (it.kind === 'text' && it.role === 'assistant' && it.model && !it.servedByFallback) {
              for (let j = idx - 1; j >= 0; j -= 1) {
                const prev = state.items[j];
                if (prev.kind === 'text' && prev.role === 'assistant') {
                  if (prev.servedByFallback) continue;   // not a model the user had
                  if (!prev.model) break;
                  const from = familyByRef.get(prev.model);
                  const to = familyByRef.get(it.model);
                  if (from && to && from.id !== to.id) {
                    modelSwitch = {
                      from: prev.answeredByName || displayModelName(prev.model),
                      to: it.answeredByName || displayModelName(it.model),
                    };
                  }
                  break;
                }
              }
            }
            return (
              <React.Fragment key={idx}>
                {modelSwitch && (
                  <div className="aoa-model-switch">
                    <div className="aoa-model-switch-line" />
                    <span className="aoa-model-switch-text">
                      <span dir="auto">{modelSwitch.from}</span>
                      <span className="aoa-model-switch-arrow"> → </span>
                      <span dir="auto">{modelSwitch.to}</span>
                    </span>
                    <div className="aoa-model-switch-line" />
                  </div>
                )}
                <TimelineRow
                  item={it}
                  activeProposalIdInPanel={activeProposalIdInPanel}
                  // Continue is offered on the conversation's LAST row only:
                  // once anything follows a paused answer, the user moved on.
                  onContinue={!sending && idx === state.items.length - 1
                    && it.kind === 'text' && it.role === 'assistant' && it.cutOff
                    && it.messageId && it.content.trim()
                    ? () => handleContinue(idx, it.messageId as string)
                    : undefined}
                />
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div className="aoa-input-shell">
        {/* Activity row — rendered ONLY while something is actually running (or
            queued). What is happening and how to stop it are ONE unit: Stop sits
            directly beside the live status text rather than across the bar from
            it, so the control is where the eye already is. When idle the row is
            gone entirely — no greyed ghost button hanging in empty space, and
            the composer sits higher. It fades in so the appearance is not a jump.

            Stop still does NOT replace Send: this composer deliberately keeps
            Send live during a turn (Claude-Code-style queueing), and swapping it
            would take that away. */}
        <div className="aoa-input-frame">
          <div className="aoa-activity-bar" data-testid="aoa-activity-bar">
            <div className="aoa-activity" data-testid="aoa-activity">
              {sending && state.activity && (
                <span className="aoa-activity-text" data-testid="aoa-activity-text">
                  <LiveStatusLine text={activityText} since={activitySince} />
                </span>
              )}
              {queueLen > 0 && (
                <span className="aoa-queue-badge" title={t('metaAgent.chat.queuedTitle')}>
                  {t('metaAgent.chat.queued', { count: queueLen })}
                </span>
              )}
            </div>
          </div>
        </div>
        {turnAccess.pending && (
          <div className="aoa-input-frame">
            {/* The turn waits here and carries on once the person allows it. */}
            <TurnGoogleAccessCard access={turnAccess.pending} onDismiss={turnAccess.dismiss} />
          </div>
        )}
        <div className="aoa-input-frame">
          {/* `isStreaming={false}` always — Claude-Code-style queueing
              means the send button stays clickable even while a turn is
              streaming. New messages are appended to the queue and drain
              automatically after each turn. The Stop control is rendered
              above (see the activity row) instead of replacing Send. */}
          <ChatInput
            onSendMessage={(content, attachments, driveFiles) => handleSend(content, attachments, driveFiles)}
            selectedModel={shownModel}
            onModelChange={pickModel}
            // Exactly the slot's "Models users can pick" ([] = every family
            // the tenant enabled), each at the tenant's pinned release.
            allowedModels={modelConfig?.allowed_models?.length ? modelConfig.allowed_models : 'all'}
            isStreaming={false}
            // Stop sits BESIDE Send, only while a turn runs — right where the
            // hand already is. `isStreaming` stays false on purpose so Send
            // remains live for Claude-Code-style queueing.
            showStopBesideSend={sending}
            onStopGeneration={handleStopGeneration}
            // The context meter chip at the composer's edge — same component
            // and place as the regular chat.
            contextMeter={
              <ContextMeter usage={contextUsage} compacting={compacting} onCompactNow={handleCompactNow} />
            }
            artifactSettings={
              brandInfo.available && brandInfo.formats.length > 0
                ? {
                    formats: brandInfo.formats,
                    branding: brandInfo.branding,
                    onToggle: handleBrandToggle,
                  }
                : undefined
            }
          />
        </div>
        {/* Auto-accept mode — applies every proposal the instant it lands,
            EXCEPT deletes, which always wait for a person. */}
        <div className="aoa-automode" data-testid="aoa-automode">
          <Switch
            size="small"
            checked={autoAccept}
            onChange={handleAutoAcceptToggle}
            aria-label={t('metaAgent.chat.autoAccept')}
          />
          <span className="aoa-automode-label">{t('metaAgent.chat.autoAccept')}</span>
          <Tooltip title={t('metaAgent.chat.autoAcceptTooltip')}>
            <span className="aoa-automode-hint">{t('metaAgent.chat.autoAcceptHint')}</span>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

function legacyModelDisplayName(raw: string): string {
  // Fallback for refs that match no family in the catalog (retired
  // concrete versions the backend still resolves). Cosmetic-only — the
  // same simple normalization the regular chat uses.
  if (!raw) return '';
  return raw
    .replace(/^anthropic\//, '')
    .replace(/^openai\//, '')
    .replace(/^google\//, '')
    .replace(/-\d{8}$/, ''); // strip dated suffixes like -20250929
}

const EmptyPrompt: React.FC = () => {
  const { t } = useLanguage();
  return (
    <div className="aoa-prompt-empty">
      <div className="aoa-prompt-empty-title">{t('metaAgent.whatShouldWeWorkOn')}</div>
      <div className="aoa-prompt-empty-sub">
        {t('metaAgent.emptyPromptSub')}
      </div>
    </div>
  );
};

// Every text block takes the direction of its own words (`dir="auto"` on the
// leaves, as the regular chat does), so an English answer under the Hebrew UI
// keeps its "?" and "." at its own end. Aligned to that start in the CSS.
const MD_COMPONENTS = {
  p: ({ node, ...props }: any) => <p className="aoa-md-p" dir="auto" {...props} />,
  pre: ({ node, ...props }: any) => <pre className="aoa-md-pre" {...props} />,
  code: ({ inline, className, children, ...props }: any) =>
    inline ? (
      <code className="aoa-md-code-inline" {...props}>
        {children}
      </code>
    ) : (
      <code className={`aoa-md-code-block ${className || ''}`} {...props}>
        {children}
      </code>
    ),
  // A list takes the direction of its own words too, so its markers hang on
  // the side its items start from.
  ul: ({ node, ...props }: any) => <ul className="aoa-md-list" dir="auto" {...props} />,
  ol: ({ node, ...props }: any) => <ol className="aoa-md-list" dir="auto" {...props} />,
  blockquote: ({ node, ...props }: any) => (
    <blockquote className="aoa-md-blockquote" {...props} />
  ),
  li: ({ node, ...props }: any) => <li dir="auto" {...props} />,
  h1: ({ node, ...props }: any) => <h3 className="aoa-md-h" dir="auto" {...props} />,
  h2: ({ node, ...props }: any) => <h4 className="aoa-md-h" dir="auto" {...props} />,
  h3: ({ node, ...props }: any) => <h4 className="aoa-md-h" dir="auto" {...props} />,
  h4: ({ node, ...props }: any) => <h5 className="aoa-md-h" dir="auto" {...props} />,
  table: ({ node, ...props }: any) => <table className="aoa-md-table" {...props} />,
  th: ({ node, ...props }: any) => <th dir="auto" {...props} />,
  td: ({ node, ...props }: any) => <td dir="auto" {...props} />,
  a: ({ node, ...props }: any) => (
    <a className="aoa-md-link" target="_blank" rel="noopener noreferrer" {...props} />
  ),
  img: () => null,
};

const ASSISTANT_AVATAR = (
  <svg width="18" height="18" viewBox="0 0 24 24">
    <path
      d="M 9 3 L 9 9 L 4.2 19.2 Q 3.7 20.5 5.2 20.5 L 18.8 20.5 Q 20.3 20.5 19.8 19.2 L 15 9 L 15 3"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
      strokeLinejoin="round" fill="none"
    />
    <line x1="8" y1="3" x2="16" y2="3"
          stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <line x1="6.5" y1="16" x2="17.5" y2="16"
          stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <circle cx="10" cy="18.2" r="0.9" fill="currentColor" />
    <circle cx="13.5" cy="17.5" r="0.7" fill="currentColor" />
  </svg>
);

const TimelineRow: React.FC<{
  item: TimelineItem;
  /** When this row's proposal is the one currently open in the side
   *  panel, render the marker with the "active" highlight so the user
   *  can correlate "open card" <-> "chat marker". */
  activeProposalIdInPanel?: string | null;
  /** Continue this paused answer — set only where it may be continued now. */
  onContinue?: () => Promise<void>;
}> = ({ item, activeProposalIdInPanel, onContinue }) => {
  const { t, direction: uiDirection } = useLanguage();
  // Files the agent wrote open in the side panel, so this row needs the same
  // inspector the chat shell uses.
  const inspector = useInspector();
  if (item.kind === 'text') {
    if (item.role === 'user') {
      // Right-aligned blue pill, matching the regular chat user bubble.
      // Attachments (if any) stack ABOVE the bubble in a tight row so
      // the files visually belong to this message — mirrors the regular
      // chat MessageItem layout.
      const attachments = item.attachments ?? [];
      const hasAttachments = attachments.length > 0;
      return (
        <div className="aoa-turn-user-row">
          <div className={`aoa-user-stack${hasAttachments ? ' has-attachments' : ''}`}>
            {hasAttachments && (
              <div className={`aoa-user-attachments${attachments.length > 1 ? ' multi' : ''}`}>
                {attachments.map((att) => (
                  <FileAttachmentCard
                    key={att.attachment_id}
                    mode="attached"
                    fileId={att.file_id}
                    fileName={att.file_name}
                    fileSize={att.file_size ?? undefined}
                    fileType={att.file_type ?? ''}
                  />
                ))}
              </div>
            )}
            {item.content && <div className="aoa-user-bubble" dir="auto">{item.content}</div>}
          </div>
        </div>
      );
    }
    // Assistant: avatar circle on the left + flat markdown content, with any
    // file the agent wrote on this turn as a chip beneath it - the same
    // affordance a regular chat puts under an agent's reply, so a document the
    // meta-agent wrote is reachable from where it was produced and not only
    // from the side panel.
    const written = item.attachments ?? [];
    return (
      <div className="aoa-turn-assistant-row">
        <div className="aoa-assistant-avatar" aria-hidden>
          {ASSISTANT_AVATAR}
        </div>
        <div className="aoa-assistant-body">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS as any}>
            {item.errorCode
              ? t(`chat.errors.${item.errorCode}`, { defaultValue: item.content })
              : stripEchoedSignals(item.content)}
          </ReactMarkdown>
          {written.length > 0 && (
            <div className={`aoa-user-attachments${written.length > 1 ? ' multi' : ''}`}>
              {written.map((att) => (
                <FileAttachmentCard
                  key={att.attachment_id}
                  mode="attached"
                  fileId={att.file_id}
                  fileName={att.file_name}
                  fileSize={att.file_size ?? undefined}
                  fileType={att.file_type ?? ''}
                  onClick={() => {
                    void inspector.artifacts.openArtifact(att.file_id);
                    inspector.setActiveArtifact(att.file_id);
                  }}
                />
              ))}
            </div>
          )}
          {item.interrupted && (
            <div className="aoa-message-stopped" data-testid="aoa-message-stopped">
              {t('chat.responseStopped')}
            </div>
          )}
          {!item.streaming && (
            <WebSources sources={item.webSources} testId="aoa-web-sources" />
          )}
          {!item.interrupted && item.limitReached && (
            <div className="aoa-message-stopped aoa-ui-note" dir={uiDirection}
                 data-testid="aoa-answer-limit-reached">
              {t('chat.answerLimitReached')}
            </div>
          )}
          {!item.interrupted && item.cutOff && !item.content.trim() && (
            <div className="aoa-message-stopped aoa-ui-note" dir={uiDirection}
                 data-testid="aoa-answer-cut-off">
              {t(item.cutOffReason === 'provider_error'
                ? 'chat.answerInterrupted' : 'chat.answerCutOffEmpty')}
            </div>
          )}
          {!item.interrupted && item.cutOff && !!item.content.trim() && (
            <PausedAnswer onContinue={onContinue} dir={uiDirection}
                          interrupted={item.cutOffReason === 'provider_error'}
                          testId="aoa-answer-paused" buttonTestId="aoa-continue" />
          )}
        </div>
      </div>
    );
  }
  if (item.kind === 'compaction') {
    // The same marker the regular chat draws at its cut.
    return (
      <div className="aoa-turn-compaction" data-testid="aoa-compaction">
        <CompactionMarker summary={item.summary} summarizedCount={item.summarizedCount} />
      </div>
    );
  }
  if (item.kind === 'activation') {
    // The SAME component a regular conversation uses, so a skill label looks
    // identical wherever the user meets it. Not animated: by the time this
    // renders the skill is already loaded.
    return (
      <div className="aoa-turn-skill" data-testid="aoa-activation-skill">
        <SkillBadges skills={[item.label]} animated={false} />
      </div>
    );
  }
  if (item.kind === 'tool_call') {
    return (
      <div className="aoa-turn-tool">
        <ToolCallBlock call={item} />
      </div>
    );
  }
  if (item.kind === 'proposal') {
    // Inline: small clickable marker that opens the full card in the
    // side inspector panel. The full ProposalCard is rendered by
    // AoaInspectorPanel reading from InspectorContext. `onProposalResolved`
    // is unused here now — the panel calls inspector.updateProposal
    // directly when the card resolves, and the chat just re-renders.
    return (
      <div className="aoa-turn-proposal">
        <ProposalMarker
          proposal={item.proposal}
          isActiveInPanel={activeProposalIdInPanel === item.proposal.proposal_id}
        />
      </div>
    );
  }
  // Persisted thinking items (legacy) are silently ignored; activity now
  // surfaces via the bottom-left indicator above the input bar.
  return null;
};
